<template>
  <q-btn
    size="32"
    round
    flat
  >
    <q-avatar size="32px">
      <img
        :src="$page.props.auth.user.avatar_url"
        referrerpolicy="no-referrer"
      >
    </q-avatar>
    <q-menu class="q-pa-md user-menu">
      <div class="column items-center q-pa-md">
        <q-avatar size="72px">
          <img
            :src="$page.props.auth.user.avatar_url"
            referrerpolicy="no-referrer"
          >
        </q-avatar>
        <div class="text-subtitle2 q-mt-md q-mb-xs">
          {{ $page.props.auth.user.name }}
        </div>
        <div class="text-caption">
          {{ $page.props.auth.user.email }}
        </div>
      </div>

      <template v-if="$page.props.shared.user_menu.workspace_groups.length > 1">
        <q-separator spaced />

        <div
          v-if="show_workspace_group_search"
          class="q-px-sm q-mb-sm"
        >
          <q-input
            v-model="workspace_group_search"
            dense
            outlined
            clearable
            hide-bottom-space
            autofocus
            :placeholder="$page.props.shared.user_menu.i18n.workspace_group_search_placeholder"
          >
            <template #prepend>
              <q-icon name="o_search" />
            </template>
          </q-input>
        </div>

        <!-- Workspace Groups -->
        <q-list style="min-width: 260px">
          <Link
            v-for="wg in filtered_workspace_groups"
            :key="wg.id"
            :href="wg.link"
          >
            <q-item
              clickable
              :active="$page.props.shared.user_menu.wg?.id === wg.id"
              class="q-py-none"
            >
              <q-item-section avatar>
                <avatar-image
                  :img-url="wg.icon_url"
                  :label="wg.name"
                  avatar-size="24px"
                  img-size="20px"
                />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ wg.name }}</q-item-label>
              </q-item-section>
            </q-item>
          </Link>

          <q-item
            v-if="filtered_workspace_groups.length === 0"
            class="q-py-none"
          >
            <q-item-section>
              <q-item-label class="text-caption text-grey">
                {{ $page.props.shared.user_menu.i18n.workspace_group_search_empty_state }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </template>

      <q-separator spaced />

      <q-list style="min-width: 260px">
        <!-- Profile -->
        <Link :href="$page.props.shared.user_menu.links.profile.link">
          <q-item
            clickable
            :active="$page.props.shared.user_menu.links.profile.is_active"
            class="q-py-none"
          >
            <q-item-section avatar>
              <q-avatar size="32px">
                <q-icon name="o_settings" />
              </q-avatar>
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ $page.props.shared.user_menu.links.profile.title }}</q-item-label>
            </q-item-section>
          </q-item>
        </Link>

        <!-- Dark mode -->
        <q-item
          clickable
          class="q-py-none"
          @click="toggle_dark_mode"
        >
          <q-item-section
            avatar
            class="flex-center"
          >
            <q-avatar size="32px">
              <q-icon name="o_dark_mode" />
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ $page.props.shared.user_menu.i18n.dark_mode }}</q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-toggle
              v-model="dark_mode"
              color="accent"
            />
          </q-item-section>
        </q-item>

        <!-- Invitations -->
        <Link :href="$page.props.shared.user_menu.links.invitations.link">
          <q-item
            :active="$page.props.shared.user_menu.links.invitations.is_active"
            clickable
            class="q-py-none"
          >
            <q-item-section
              avatar
              class="flex-center"
            >
              <q-avatar size="32px">
                <q-icon name="o_person_pin" />
              </q-avatar>
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ $page.props.shared.user_menu.links.invitations.title }}</q-item-label>
            </q-item-section>
            <q-item-section side>
              <!-- Chip with number of invitations -->
              <q-chip
                class="q-px-sm text-white"
                dense
                flat
                :color="$page.props.shared.user_menu.invitation_count > 0 ? 'positive' : 'grey'"
              >
                {{ $page.props.shared.user_menu.invitation_count }}
              </q-chip>
            </q-item-section>
          </q-item>
        </Link>
      </q-list>

      <q-separator spaced />

      <q-list style="min-width: 260px">
        <!-- Subscriptions -->
        <Link :href="$page.props.shared.user_menu.links.subscriptions.link">
          <q-item
            :active="$page.props.shared.user_menu.links.subscriptions.is_active"
            class="q-py-none"
            clickable
          >
            <q-item-section
              avatar
              class="flex-center"
            >
              <q-avatar size="32px">
                <q-icon name="o_credit_card" />
              </q-avatar>
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ $page.props.shared.user_menu.links.subscriptions.title }}</q-item-label>
            </q-item-section>
          </q-item>
        </Link>

        <!-- Invoices -->
        <Link :href="$page.props.shared.user_menu.links.invoices.link">
          <q-item
            :active="$page.props.shared.user_menu.links.invoices.is_active"
            class="q-py-none"
            clickable
          >
            <q-item-section
              avatar
              class="flex-center"
            >
              <q-avatar size="32px">
                <q-icon name="o_receipt" />
              </q-avatar>
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ $page.props.shared.user_menu.links.invoices.title }}</q-item-label>
            </q-item-section>
          </q-item>
        </Link>
      </q-list>

      <q-separator spaced />

      <q-list style="min-width: 260px">
        <!-- Termes & Conditions -->
        <q-item
          class="q-py-none"
          clickable
          tag="a"
          :href="$page.props.shared.user_menu.links.terms.link"
          target="_blank"
        >
          <q-item-section
            avatar
            class="flex-center"
          >
            <q-avatar size="32px">
              <q-icon name="o_description" />
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label>
              {{ $page.props.shared.user_menu.links.terms.title }}
            </q-item-label>
          </q-item-section>
        </q-item>

        <!-- Aide et Assistance -->
        <q-item
          class="q-py-none"
          clickable
          tag="a"
          :href="$page.props.shared.user_menu.links.help.link"
          target="_blank"
        >
          <q-item-section
            avatar
            class="flex-center"
          >
            <q-avatar size="32px">
              <q-icon name="o_help" />
            </q-avatar>
          </q-item-section>
          <q-item-section>
            {{ $page.props.shared.user_menu.links.help.title }}
          </q-item-section>
        </q-item>

        <!-- Language -->
        <q-item class="q-py-none">
          <q-item-section
            avatar
            class="flex-center"
          >
            <q-avatar size="32px">
              <q-icon name="o_language" />
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ $page.props.shared.user_menu.i18n.language }}</q-item-label>
          </q-item-section>
          <q-item-section
            side
            class="flex flex-end justify-end col-shrink"
          >
            <q-select
              v-model="$page.props.shared.user_menu.i18n.language_current"
              :options="$page.props.shared.user_menu.languages"
              dense
              borderless
              emit-value
              map-options
              options-dense
              @update:model-value="switch_locale"
            />
          </q-item-section>
        </q-item>
      </q-list>

      <q-separator spaced />

      <q-btn
        v-close-popup
        color="secondary"
        :label="$page.props.shared.user_menu.logout"
        push
        flat
        block
        class="full-width q-mt-sm btn-underline"
        :href="$page.props.auth.logout"
      />
    </q-menu>

    <!-- Display workspace group badge when user has more than one -->
    <q-badge
      v-if="$page.props.shared.user_menu.wg"
      rounded
      align="bottom"
      floating
      color="transparent"
      style="top: 22px; left: 22px"
    >
      <avatar-image
        class="user-menu-badge"
        avatar-size="16px"
        img-size="16px"
        :img-url="$page.props.shared.user_menu.wg.icon_url"
        :label="$page.props.shared.user_menu.wg.name"
      />
    </q-badge>
  </q-btn>
</template>

<script>
import AvatarImage from '@/shared/avatar_image.vue';
import { Link } from '@inertiajs/vue3';

export default {
  components: {
    AvatarImage,
    Link,
  },
  props: {
    locale: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      dark_mode: false,
      workspace_group_search: '',
    };
  },
  computed: {
    show_workspace_group_search() {
      const groups = this.$page.props.shared.user_menu.workspace_groups || [];
      return groups.length > 10;
    },
    filtered_workspace_groups() {
      const groups = this.$page.props.shared.user_menu.workspace_groups || [];
      const query = this.workspace_group_search?.trim().toLowerCase();

      if (!query) {
        return groups;
      }

      return groups.filter((group) => group.name.toLowerCase().includes(query));
    },
  },
  watch: {
    dark_mode(new_value) {
      this.$q.dark.set(new_value);
      this.$intercom.launcher_dark_mode(new_value);
      localStorage.setItem("dark_mode", new_value);
    }
  },
  created() {
    this.dark_mode = localStorage.getItem("dark_mode") === 'true';
    this.$q.dark.set(this.dark_mode);
  },
  methods: {
    toggle_dark_mode() {
      this.dark_mode = !this.dark_mode;
    },
    switch_locale(link) {
      this.$inertia.get(link, {}, {preserveState: true});
      // Confirmation Dialog
      //
      // This dialog no longer seems necessary.
      // Previously, the locale switch triggered a `location.reload()`, causing a hard refresh.
      // With the location reload removed, the dialog has become redundant.
      //
      // this.$q.dialog({
      //   title: this.$page.props.shared.user_menu.i18n.confirm_switch_locale_title,
      //   message: this.$page.props.shared.user_menu.i18n.confirm_switch_locale_message,
      //   cancel: true,
      //   persistent: true,
      // }).onOk(() => {
      //   this.$inertia.get(link);
      // }).onCancel(() => {
      //   this.locale = this.$props.locale;
      // })
    }
  }
};
</script>

<style scoped>
/* set min height to 36px for any q-item in the user-menu menu */
.user-menu .q-item {
  min-height: 36px;
}
</style>
